import athenaConfig from './lib/config';
import logger from './logger';
import AmplitudeUtils from './lib/utils/amplitude-utils';
import customizeController from './lib/customizers';
import { APP_TYPES, ERROR_MESSAGES, I18N_MAPPING, LanguageType } from './lib/constants';
import {
  applyContentHidingForCustomization,
  isPrimaryAuthController,
  isRequestFromSamlClient
} from './lib/utils/widget-facade';
import { transformUsername } from './lib/utils/user-utils';
import { withErrorConsumed } from './lib/utils/common-utils';

const { portalClientId, oieEnabled, manageProfileClientSettings } = athenaConfig;
const manageProfileUrl = manageProfileClientSettings['default'].userProfileUrl;

const aosw = {
  getTitleForApp: (context: any) => {
    if (!context) return 'Sign In';
    const isSpanish = config?.language?.includes('es');
    const appName = settings.appLabel;
    if (appName) {
      if (!settings.isAuthnWithLwaClientId && portalClientId === settings.clientId) {
        if (isSpanish) {
          return 'Tu Portal del Paciente';
        }
        return 'Your Patient Portal';
      }
      return global.settings.displayName || config.i18n['en']['primaryauth.appname'] || appName;
    }
    return 'Sign In';
  },

  getPrimaryAuthDescription: () => {
    if (config?.language?.includes('es')) {
      return 'Inicia sesión para conectarte a';
    }
    return [ APP_TYPES.PATIENT, APP_TYPES.ADJACENT_SERVICES_USERS ].includes(settings.appType!)
      ? 'Enter your email and password to log in to'
      : 'Enter your username and password to log in to';
  },

  getUsernameLabel: () => {
    const language = config?.language;
    if (language?.includes('es')) {
      return settings.appType === APP_TYPES.ATHENANET_USER ? 'Nombre de usuario' : 'Dirección de correo electrónico';
    }
    else {
      return [ APP_TYPES.PATIENT, APP_TYPES.ADJACENT_SERVICES_USERS ].includes(settings.appType!)
        ? 'Email Address'
        : 'Username';
    }
  },

  getConsentRequiredText: (manageProfileUrl: string, appName: string | undefined = undefined) => {
    // 0 is replaced okta-widget using i18n and shouldn't be passed while setting up through i18n
    // with OIE, we need to set this text dynamically, hence, the final message should be populated with app name
    appName = appName || '{0}';
    const disclaimerText = `
    <p align="center">
    <b>${appName}</b> is asking to access parts of your medical record:
    </p>
    <br><br>
    <p align="left" class="fe_f_all fe_c_heading--subsection" style="font-family: 'Source Sans Pro', arial, sans-serif" font-size: 18px;>
    <b>Take control of your health data.</b></p>
    <br>
    <p align="left" class="fe_f_all" style="font-family: 'Source Sans Pro', arial, sans-serif" font-size: 16px;>
    We know having control over your health is important, which is why athenahealth allows you to choose which apps you use to interact with your health data.</p>
    <br>
    <p align="left" class="fe_f_all" style="font-family: 'Source Sans Pro', arial, sans-serif" font-size: 16px;>
    Please note you are responsible for understanding how apps will use your data and grant the permissions below at your own risk. Visit <a href="https://www.athenahealth.com/patient-login/faq" data-se="privacy-link" style="color:#0366d6" class="link js-help-link" target="_blank">our patient FAQs</a> to learn more about managing access to your records.</p>
    <br><hr style="border-top: 1px solid #d3d4d4; display:block"><br>
    <p align="left" class="fe_f_all fe_c_heading--subsection" style="font-family: 'Source Sans Pro', arial, sans-serif" font-size: 18px;>
    <b>Grant permissions to this app</b></p>
    <br>
    <p align="left" class="fe_f_all" style="font-family: 'Source Sans Pro', arial, sans-serif" font-size: 16px;>
    You can uncheck any permissions you don’t want to share with this app below. You can change these choices later in your <a href="${manageProfileUrl}" data-se="profile-link" style="color:#0366d6" class="link js-help-link" target="_blank">athenahealth profile</a> privacy settings.</p>
    `;
    const disclaimerTextEs = `
    <p align="center">
    <b>${appName}</b> está solicitando acceder a partes de su expediente médico:
    </p>
    <br><br>
    <p align="left" class="fe_f_all fe_c_heading--subsection" style="font-family: 'Source Sans Pro', arial, sans-serif" font-size: 18px;>
    <b>Tome el control de sus datos de salud.</b></p>
    <br>
    <p align="left" class="fe_f_all" style="font-family: 'Source Sans Pro', arial, sans-serif" font-size: 16px;>
    Sabemos que tener control sobre sus datos de salud es importante, por eso athenahealth le permite elegir qué aplicaciones usa para interactuar con sus datos de salud.</p>
    <br>
    <p align="left" class="fe_f_all" style="font-family: 'Source Sans Pro', arial, sans-serif" font-size: 16px;>
    Tenga en cuenta que usted es responsable de comprender cómo las aplicaciones utilizarán sus datos y otorgue los permisos a continuación bajo su propio riesgo. Visita nuestras <a href="https://www.athenahealth.com/patient-login/faq" data-se="privacy-link" style="color:#0366d6" class="link js-help-link" target="_blank">preguntas frecuentes de los pacientes</a> para obtener más información sobre la gestión del acceso a tus historiales.</p>
    <br><hr style="border-top: 1px solid #d3d4d4; display:block"><br>
    <p align="left" class="fe_f_all fe_c_heading--subsection" style="font-family: 'Source Sans Pro', arial, sans-serif" font-size: 18px;>
    <b>Otorgar permisos a esta aplicación</b></p>
    <br>
    <p align="left" class="fe_f_all" style="font-family: 'Source Sans Pro', arial, sans-serif" font-size: 16px;>
    Puede desmarcar los permisos que no desea compartir con esta aplicación a continuación. Puede cambiar estas opciones más adelante en la configuración de privacidad de su <a href="${manageProfileUrl}" data-se="profile-link" style="color:#0366d6" class="link js-help-link" target="_blank">perfil de athenahealth</a>.</p>
    `;
    if (config?.language?.includes('es')) {
      return disclaimerTextEs;
    }
    else {
      return disclaimerText;
    }
  },

  getConsentRequiredDescription: () => {
    return ' ';
  },

  getSkipConsentButtonText: () => {
    if (config?.language?.includes('es')) {
      return 'Disminución';
    }
    else {
      return 'Decline';
    }
  },

  getAllowConsentButtonText: () => {
    if (config?.language?.includes('es')) {
      return 'Permitir Acceso';
    }
    else {
      return 'Allow Access';
    }
  },

  flatten: (jsonObject: Record<string, any>) => {
    const result: Record<string, any> = {};
    for (const key in jsonObject) {
      if (jsonObject[key] && typeof jsonObject[key] === 'object' && !(jsonObject[key] instanceof Array)) {
        const innerObject = aosw.flatten(jsonObject[key]);
        for (const innerKey in innerObject) {
          result[`${key}.${innerKey}`] = innerObject[innerKey];
        }
      }
      else {
        result[key] = jsonObject[key];
      }
    }
    return result;
  },

  getSelectedLanguage: withErrorConsumed(() => {
    const parsedUrl = new URL(window.location.href);
    return parsedUrl.searchParams.get('ui_locales') as LanguageType;
  }),
  getIdpDiscoveryFeatureValue: (requestContext: any) => {
    if (settings.appType) {
      return (
        [ APP_TYPES.ATHENANET_USER, APP_TYPES.ATHENISTA ].includes(settings.appType) && !settings.disableIdpDiscovery
      );
    }
    // TODO Get rid of this condition once saml builder tool supports app config creation
    return isRequestFromSamlClient(requestContext);
  },
  customizeSignInWidget,
  setupHooks,
};

function customizeSignInWidget(config: any) {
  const requestContext = global.OktaUtil.getRequestContext();
  const loginContainer = document.getElementById('okta-login-container');
  if (loginContainer) {
    // a top margin for applogin-container we are hiding
    loginContainer.style.marginTop = '0px';
  }
  if (!config) {
    return;
  }
  try {
    // Disable security beacon to avoid overflow in primary-auth
    config.features.securityImage = false;
    config.features.rememberMe = false;
    // set username transformer function
    config.transformUsername = transformUsername;
    config.features = { ...config.features, idpDiscovery: aosw.getIdpDiscoveryFeatureValue(requestContext) };

    const isPatientApp = settings.appType === APP_TYPES.PATIENT;

    const language: LanguageType = settings.language || aosw.getSelectedLanguage() || config.language;
    config.language = language;
    config.i18n[language] = config.i18n[language] || {};

    config.i18n[language] = {
      ...config.i18n[language],
      ...I18N_MAPPING[language],
      ...(settings?.i18n?.[language] ? aosw.flatten(settings?.i18n?.[language]) : {}),
    };
    config.i18n[language]['primaryauth.username.label'] = aosw.getUsernameLabel();
    config.i18n[language]['primaryauth.titlemessage'] = aosw.getPrimaryAuthDescription();

    const appTitle = aosw.getTitleForApp(requestContext);
    config.i18n[language]['primaryauth.title'] = appTitle;
    config.i18n[language]['oie.password.challenge.title'] = appTitle;
    if (isPatientApp) {
      if (!config.i18n[language]['consent.required.text']) {
        config.i18n[language]['consent.required.text'] = aosw.getConsentRequiredText(manageProfileUrl);
        config.i18n[language]['oie.consent.scopes.granular.title'] = aosw.getConsentRequiredText(manageProfileUrl);
      }
      if (!config.i18n[language]['consent.required.description']) {
        config.i18n[language]['consent.required.description'] = aosw.getConsentRequiredDescription();
        config.i18n[language]['oie.consent.scopes.granular.description'] = aosw.getConsentRequiredDescription();
      }
      if (!config.i18n[language]['consent.required.cancelButton']) {
        config.i18n[language]['consent.required.cancelButton'] = aosw.getSkipConsentButtonText();
      }
      if (!config.i18n[language]['consent.required.consentButton']) {
        config.i18n[language]['consent.required.consentButton'] = aosw.getAllowConsentButtonText();
      }
    }
    //Now that we've populated the i18n over to config, we can delete this
    delete settings.i18n;
  }
  catch (error) {
    logger.log(error);
  }
}

function setupHooks(widget: any) {
  const requestContext = global.OktaUtil.getRequestContext();
  widget.on('ready', function () {
    applyContentHidingForCustomization();
    // The Widget is ready for user input
    AmplitudeUtils.logCustomEvent('Landing', {});
    if (window.innerWidth <= 1023) {
      AmplitudeUtils.logCustomEvent('SuppressMarketingContent', {});
    }
  });

  widget.on('afterRender', async function (context: any) {
    if (context) {
      await customizeController({
        requestContext,
        context,
        config: global.config,
        oktaSignIn: global.oktaSignIn,
        settings: global.settings,
      });
    }
  });

  widget.on('afterError', afterError);
}

function afterError(context: any, error: any) {
  try {
    // in classic engine, password requirements not met error contains one extra dot (.) at the end
    const responseJson = error?.xhr?.responseJSON;
    const isFormValidationError =
      responseJson?.errorCauses?.[0]?.['errorSummary']?.indexOf(ERROR_MESSAGES['password.passwordRequirementsNotMet']) >
      -1;

    const sysErrorRegex = /^5\d\d$/;
    const errorMessage = oieEnabled ? error?.errorSummary : error?.message;
    const isAuthApiError = context && error?.name === 'AuthApiError';

    const isInvalidCredentialsError = oieEnabled
      ? responseJson?.errorSummaryKeys?.includes('errors.E0000004')
      : isAuthApiError && responseJson?.errorCode === 'E0000004';

    const isNotSysErr = error?.statusCode !== 429 && !sysErrorRegex.test(error.statusCode);

    logErrorAmplitudeEvent({
      isFormValidationError,
      errorMessage,
      isInvalidCredentialsError,
      isNotSysErr,
      error,
      isAuthApiError,
      context,
    });
  }
  catch (e) {
    // do nothing on errors
  }
}

function logErrorAmplitudeEvent({
  isFormValidationError,
  errorMessage,
  isInvalidCredentialsError,
  isNotSysErr,
  context,
  isAuthApiError,
  error,
}: {
  isFormValidationError: boolean;
  errorMessage: any;
  isInvalidCredentialsError: any;
  isNotSysErr: boolean;
  context: any;
  error: any;
  isAuthApiError: boolean;
}) {
  if (isInvalidCredentialsError && isPrimaryAuthController(context.controller)) {
    AmplitudeUtils.logCustomEvent('InvalidCredentials', {
      errorMessage,
      ...context,
    });
  }
  else {
    const amplitudeProperties = { errorMessage, ...context, error };
    if (oieEnabled) {
      AmplitudeUtils.logCustomEvent('ApiError', amplitudeProperties);
    }
    else {
      if (isAuthApiError && isNotSysErr) {
        AmplitudeUtils.logCustomEvent('UserError', amplitudeProperties);
      }
      else if (!isFormValidationError) {
        AmplitudeUtils.logCustomEvent('SystemError', amplitudeProperties);
      }
    }
  }
}

export default aosw;
